import { dispatchWindowOpen } from '@/common/document-event/dispatch';
import { getCookie, removeCookie, removeStorage, getStorage } from '@lhb/cache';
import { isNotEmpty, parseArrayToString } from '@lhb/func';
import dayjs from 'dayjs';

/*
 * 项目业务级功能方法汇总文件
 * 通用性公共方法记得迭代到 @lhb/func 方法库
 */

/**
 *
 * @param dataSource // 需要遍历的数组
 * @param rowKeys // 数组中节点的key
 * @param rowKey // 组成key的字段
 * @param childrenColumnName
 * @param needAllKeys // 是否需要获取全部的key-默认为false
 * @returns rowKeys // 数组中节点的key
 */

export function getKeys(
  dataSource: any[],
  rowKeys: any[],
  rowKey: string,
  childrenColumnName = 'children',
  needAllKeys = false
) {
  for (let i = 0; i < dataSource.length; i++) {
    const item = dataSource[i];
    if (item[childrenColumnName] && item[childrenColumnName].length) {
      // 不可选的选项不获取key
      !item.disabled && rowKeys.push(item[rowKey]);
      getKeys(item[childrenColumnName], rowKeys, rowKey, childrenColumnName, needAllKeys);
    } else {
      needAllKeys && !item.disabled && rowKeys.push(item[rowKey]);
    }
  }
  return rowKeys;
}

/**
 * 退出登录
 */
export function logout() {
  const customToken = window.customToken || 'newPmsToken';
  removeCookie(customToken);
  removeStorage(customToken);
  removeStorage('pageFilters');
  // singleLogout();
  setTimeout(() => {
    if (window.__POWERED_BY_QIANKUN__) { // 微应用
      // 跳转到kunlun去做登录
      window.location.href = `${process.env.KUNLUN_URL}?origin=${encodeURIComponent(window.location.href)}`;
    } else {
      window.location.reload();
    }
  }, 100);
}

// 逆地理编码(经纬度转换成具体地址)
/**
 * @param {Array} lnglat 经纬度
 * @param {String} cityName 城市名
 * @param {Boolean} onlyAddress 是否仅返回具体地址
 */
export function getLngLatAddress(geocoder, lnglat, onlyAddress = true) {
  return new Promise((resolve, reject) => {
    geocoder.getAddress(lnglat, (status, result) => {
      if (status === 'complete' && result && result.regeocode) {
        const { regeocode } = result;
        if (onlyAddress) {
          const { formattedAddress } = regeocode || {};
          resolve(formattedAddress);
        } else {
          resolve(regeocode);
        }
      } else {
        reject(new Error(`根据经纬度查询地址失败`));
      }
    });
  });
}
/**
 * @description: 下载/预览文件
 * @param {type} name 文件名
 * @param {type} url 文件路径
 * @param {type} preview 是否预览，默认直接下载：true 预览，false 直接下载
 * @param {type} downloadUrl 文件下载路径，不传则取自 url
 * @param {type} anyFileExt 是否任何文件都可以被预览，默认true（主要是针对没有文件名的七牛图片）
 * 如果预览文件类型可能有多种情况（可能是 jpg 可能是 excel），需求传入 name 并且设置 anyFileExt = false，根据文件后缀判断文件类型能否被预览
 * @return:
 */
export function downloadFile({ name = '', url }: { name?: string; url: string }) {
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', name);
  link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * @description: 对象数组合并，并去重
 * @param {type} arr1 数组1
 * @param {type} arr2 数组2
 * @param {type} key  去重的唯一key值，默认 id
 * @return:
 */
export function concatJsonArray(arr1: Array<any>, arr2: Array<any>, key = 'id') {
  const hash = {};
  return arr1.concat(arr2).reduce((result, cur) => {
    const value = cur[key];
    if (value && !hash[value]) {
      hash[value] = true;
      result.push(cur);
    }
    return result;
  }, []);
}

/**
 *
 * @param data 文件流
 * @param fileName 下载文件名称
 * @param type 下载文件类型，默认为excel
 */
export const downloadFileStream = (data: any, fileName: string, type?: string) => {
  const blob = new Blob([data], { type: type || 'application/vnd.ms-excel' });
  // 这里就是创建一个a标签，等下用来模拟点击事件
  const a = document.createElement('a');
  // 兼容webkix浏览器，处理webkit浏览器中href自动添加blob前缀，默认在浏览器打开而不是下载
  const URL = window.URL || window.webkitURL;
  // 根据解析后的blob对象创建URL 对象
  const herf = URL.createObjectURL(blob);
  // 下载链接
  a.href = herf;
  // 下载文件名,如果后端没有返回，可以自己写a.download = '文件.pdf'
  a.download = fileName;
  document.body.appendChild(a);
  // 点击a标签，进行下载
  a.click();
  // 收尾工作，在内存中移除URL 对象
  document.body.removeChild(a);
  window.URL.revokeObjectURL(herf);
};
/*
* 用来把接口返回的选项数据，name，id转为 label value
* 我们的接口爱用 name id，antd默认 label，value。 甚至radio这种都不允许改key，贼烦，故有此方法函数
*/
function _mapTree(org) {
  const haveChildren = Array.isArray(org.children) && org.children.length;
  return {
    label: org.name,
    value: org.id,
    children: haveChildren ? org.children.map(item => _mapTree(item)) : []
  };
}
export function refactorSelection(selection: any[] = []) {
  return selection.map((item: any) => {
    return _mapTree(item);
  });
}

/**
 * [showInvalidFieldMsg 解析el-form校验未通过的字段信息]
 * @param  {[type]} object    未通过校验的字段
 * @param  {[type]} maxLength 提示内容的最大个数，默认 -1无限制
 * @return {[type]}        [description]
 */
export function showInvalidFieldMsg(errorFields, maxLength = -1) {
  if (!Array.isArray(errorFields) || !errorFields.length) {
    return null;
  }
  const arr: any = [];
  errorFields.forEach(item => {
    const data = parseArrayToString(item.errors);
    if (data) {
      arr.push(data);
    }
  });

  const messages = maxLength === -1 ? arr : arr.slice(0, maxLength);
  window.microMessage.warning(parseArrayToString(messages));

  return messages;
}


/**
 *
 * @param value 值
 * @returns 值如果为空则显示-
 */
export const valueFormat = (value: any) => {
  return isNotEmpty(value) ? value : '-';
};

export const getTokenMerge = () => {
  // 根据父应用传入的tokenKey从cookie中获取
  const customToken = window.customToken || 'newPmsToken';
  return getCookie(customToken) || getStorage(customToken);
};

/**
 * @description: 处理日期为 年月日
 * @param {*} date dayjs 格式的日期，一般来自于 FormDatePicker、FormRangePicker 组件，如 dayjs('2023-01-01')
 * @param {*} format 格式，如 YYYY-MM-DD、YYYY-MM-DD HH:mm
 * @return {*}
 * decodeDate(dayjs('2023-01-01'));
  decodeDate(dayjs('2023-01-01 22:21:20'));
  decodeDate(dayjs('2023-01-01'), 'YYYY-MM-DD HH:mm');
  decodeDate(dayjs('2023-01-01 22:21:20'), 'YYYY-MM-DD HH:mm');
  decodeDate(dayjs('2023-01-01 22:21:20'), 'YYYY-MM-DD HH:mm:ss');
 */
export const decodeDate = (date, format = 'YYYY-MM-DD') => {
  return isNotEmpty(date) ? dayjs(date).format(format) : null;
};

/**
 * @description: 处理日期为 dayjs
 * @param {*} date 日期，如 2023-01-01
 * @return {*}
 * encodeDate('2023-01-01');
  encodeDate('2023-01-01 22:21:20');
 */
export const encodeDate = (date) => {
  return isNotEmpty(date) ? dayjs(date) : null;
};


/**
 * 滚动到第一个必填未填项
 */
export function scrollToError(trueDocument) {
  setTimeout(() => {
    const errorLists = trueDocument.querySelector('.resant-form-item-has-error');
    errorLists?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 0);
};



/**
 * 计算图表类
 */
export function echartsFormatUnit(data) { // data | Array
  const minVal = Math.min.apply(Math, data); // 最小值
  const unit = 10000;
  if ((minVal / unit) >= 1) { // 以万为单位
    return {
      name: 'w',
      value: 10000
    };
  } else {
    return {
      name: '',
      value: 1
    };
  }
}

/**
 * 将数字转成k、w结尾
 * @param num 值
 * @returns 1000转化为1k，10000转为1w,10000000转为1kw
 */
export function formatNumber(num: number) {
  // return num >= 1e3 && num < 1e4 ? (num / 1e3).toFixed(1) + 'k' : num >= 1e4 ? (num / 1e4).toFixed(1) + 'w' : num;
  if (num >= 1e3 && num < 1e4) {
    return (num / 1e3).toFixed(1) + 'k';
  } else if (num >= 1e4 && num < 1e8) {
    return (num / 1e4).toFixed(1) + 'w';
  } else if (num >= 1e8) {
    return (num / 1e8).toFixed(1) + '亿';
  } else {
    return num;
  }
}

/**
 * 打开高德地图网页
 * @param longitude 经度
 * @param latitude 纬度
 * @param address 地名
 */
export function openAMap(longitude: string|number, latitude:string|number, address:string) {
  return dispatchWindowOpen(`https://ditu.amap.com/regeo?lng=${longitude}&lat=${latitude}&name=${address}&src=uriapi&callnative=1&innersrc=uriapi`);
}

/**
 * @description 用来转包服务端接口获取到的 tabs 数组格式，用于V2Tab组件
 * @param {Array} items 操作列表
 * @param {String} childrenKey 子节点的key
 */
export function refactorTabs(items: any[] = [], childrenKey: string = 'children') {
  const result: any = [];
  if (!(Array.isArray(items) && items.length)) return result;
  items.forEach((item) => {

    result.push({
      ...item,
      key: item.value,
      children: item[childrenKey] ? refactorTabs(item[childrenKey]) : []
    });
  });
  return result;
}

/**
 * @description 根据对象数组中某个属性值进行排序（属性值必须是number）,默认降序
 * @param {Array} arr 需要排序的数组
 * @param {string} property 排序时依赖的字段名
 * @param {any} desc 是否降序
 * @return {any} 排序后的对象数组
 */
export function targetValSort(
  arr: Array<any>,
  property: string,
  desc = true
) {
  return arr.sort((a: any, b: any) => {
    const val1 = +a[property];
    const val2 = +b[property];
    return desc ? val2 - val1 : val1 - val2;
  });
}

// 判断浏览区是否支持canvas
export function isSupportCanvas() {
  const elem = document.createElement('canvas');
  return !!(elem.getContext && elem.getContext('2d'));
}

/**
 * @description 搜索框搜索下拉框前端匹配通用方法
 */
export function formatSelectOptions(input: any, option: any) {
  if (!option || !option.labelName || !input) {
    return false;
  }
  const labelNameLower = option.labelName.toLowerCase();
  const inputLower = input.toLowerCase();

  return labelNameLower.indexOf(inputLower) >= 0;
}
